import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { List, Plus } from '@phosphor-icons/react';

import { AppNavDrawer } from './AppNavDrawer';
import { cn } from '../../utils/cn';
import pt from '../../locales/pt';
import {
  ALL_NAV_ITEMS,
  BOTTOM_NAV_ITEMS,
  getMobileMenuSections,
  isNavItemActive,
} from './navItems';
import { QuickActionsSheet } from './QuickActionsSheet';
import { StackedNavItem } from './StackedNavItem';
import { useFABAction } from './useFABAction';

/** Slot de 1/5 da barra (`slot-N` no .pen): fill_container + centrado. */
const SLOT_CLASS = 'flex min-w-0 flex-1 items-center justify-center';

function BottomNavLink({ item }) {
  const location = useLocation();
  const { to, label, shortLabel, icon: Icon, end } = item;
  const active = isNavItemActive(location.pathname, item);
  const tabLabel = shortLabel ?? label;

  return (
    <NavLink
      to={to}
      end={end}
      aria-current={active ? 'page' : undefined}
      aria-label={shortLabel && shortLabel !== label ? label : undefined}
      title={label}
      className="flex w-full min-w-0"
    >
      <StackedNavItem icon={Icon} label={tabLabel} active={active} />
    </NavLink>
  );
}

/**
 * Barra de navegação inferior mobile — `ds/nav/bottom-bar--mobile` no .pen:
 * 5 slots iguais (Início · Despesas · FAB · Património · Menu), superfície de
 * card com borda superior e FAB absoluto a subir 14px acima da barra.
 */
export function BottomNav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const location = useLocation();

  const menuSections = getMobileMenuSections();
  const fab = useFABAction(() => setQuickOpen(true));
  const menuActive = ALL_NAV_ITEMS.some(
    (item) =>
      !BOTTOM_NAV_ITEMS.some((b) => b.to === item.to) &&
      isNavItemActive(location.pathname, item),
  );

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <nav
        data-ds-id="ds/nav/bottom-bar"
        // eslint-disable-next-line no-restricted-syntax -- altura 76px do .pen, sem token de spacing
        className="fixed inset-x-0 bottom-0 z-30 h-[76px] border-t border-border bg-card p-space-2 md:hidden"
        aria-label="Navegação principal"
      >
        <ul className="flex h-full w-full items-end">
          <li className={SLOT_CLASS}>
            <BottomNavLink item={BOTTOM_NAV_ITEMS[0]} />
          </li>
          <li className={SLOT_CLASS}>
            <BottomNavLink item={BOTTOM_NAV_ITEMS[1]} />
          </li>
          <li className={SLOT_CLASS} aria-hidden />
          <li className={SLOT_CLASS}>
            <BottomNavLink item={BOTTOM_NAV_ITEMS[2]} />
          </li>
          <li className={SLOT_CLASS}>
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-expanded={menuOpen}
              aria-haspopup="dialog"
              aria-controls="mobile-nav-menu"
              aria-current={menuOpen || menuActive ? 'page' : undefined}
              className="flex w-full min-w-0"
            >
              <StackedNavItem
                icon={List}
                label={pt.nav.menu}
                active={menuOpen || menuActive}
              />
            </button>
          </li>
        </ul>

        {/* ds/nav/bottom-fab — absoluto, 56×56, 14px acima do topo da barra */}
        <button
          type="button"
          onClick={fab.action}
          className={cn(
            'absolute left-1/2 -top-3.5 flex h-14 w-14 -translate-x-1/2 items-center justify-center',
            'rounded-ds-full bg-sage-700 text-sage-50 ring-4 ring-background transition-transform active:scale-95 dark:bg-sage-800',
            // eslint-disable-next-line no-restricted-syntax -- sombra do .pen (offset/blur) com token de cor
            'shadow-[0_6px_18px] shadow-sage-glow',
          )}
          aria-label={fab.label}
        >
          <Plus className="h-6 w-6" weight="bold" aria-hidden />
        </button>
      </nav>

      <QuickActionsSheet open={quickOpen} onOpenChange={setQuickOpen} />

      <AppNavDrawer
        id="mobile-nav-menu"
        open={menuOpen}
        onOpenChange={setMenuOpen}
        sections={menuSections}
      />
    </>
  );
}
